import { useContext } from 'react';

import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';
import { FEATURE_TO_TITLE } from 'hooks/dialogs/cluster/constants';
import { FeatureName, FeatureSelection } from 'hooks/dialogs/cluster/types';

import { ConnectionProgressItem } from '../../../../_components/new-onboarding';
import { getKentFeatureSelection } from '../../../../_hooks/useFeatureSelection';
import { OnboardClusterContext } from '../../../OnboardClusterProvider';

type ConnectionProgress = {
  clusterConnection: ConnectionProgressItem;
  featureItems: ConnectionProgressItem[];
};

export const useConnectionProgress = (): ConnectionProgress => {
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();
  const { isClusterConnected, featureEnablementStatus, featureSelection } =
    useContext(OnboardClusterContext);

  const clusterConnection: ConnectionProgressItem = {
    key: 'cluster',
    label: 'Cluster connection',
    status: isClusterConnected ? 'completed' : 'in_progress',
  };

  const featureItems: ConnectionProgressItem[] = [];

  const featureSelectionToDisplay: FeatureSelection = featureSelection.kent
    ? // Kent onboarding always displays a fixed set of connection items.
      // Feature selection state is preserved to avoid resetting user choices when switching autoscaler type
      // so we use the kent defaults here instead.
      getKentFeatureSelection()
    : featureSelection;

  for (const [feature, selected] of Object.entries(
    featureSelectionToDisplay
  ) as [FeatureName, boolean][]) {
    if (!selected) continue;
    if (feature === 'security' && isSecuritySunsetEnabled) continue;

    let status: ConnectionProgressItem['status'];
    if (featureEnablementStatus[feature] === true) {
      status = 'completed';
    } else if (isClusterConnected) {
      status = 'in_progress';
    } else {
      status = 'pending';
    }

    featureItems.push({
      key: feature,
      label: FEATURE_TO_TITLE[feature],
      status,
    });
  }

  return { clusterConnection, featureItems };
};
